/** Backend hat_ozeti / ag_toplami ile aynı şekil — özet ekranı için. */

export const CURRENT_ID = "mevcut";

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Özet ekranı grafiği için kompakt saatlik profil (yük ≈ koltuk × max_doluluk). */
export function compactSaatlikProfil(profil) {
  const nested = profil || {};
  const saatlik = Array.isArray(nested.saatlik) ? nested.saatlik : [];
  return {
    hedef_ustu_yol_orani: nested.hedef_ustu_yol_orani,
    max_doluluk: nested.max_doluluk,
    max_doluluk_yeri: nested.max_doluluk_yeri || null,
    saatlik: saatlik.map((s) => {
      const koltuk = Number(s?.koltuk);
      const maxDol = Number(s?.max_doluluk);
      const yuk =
        Number.isFinite(koltuk) && Number.isFinite(maxDol) ? koltuk * maxDol : null;
      return {
        saat: Number(s?.saat),
        yon: String(s?.yon || "").toLowerCase().startsWith("gid") ? "gidis" : "donus",
        sefer: Number(s?.sefer) || 0,
        max_doluluk: Number.isFinite(maxDol) ? maxDol : null,
        yuk: yuk != null && Number.isFinite(yuk) ? Math.round(yuk) : null,
      };
    }).filter((s) => Number.isFinite(s.saat)),
  };
}

function senaryoOzeti(blok) {
  if (!blok || blok.hata != null) return { hata: blok?.hata || "sonuc_yok" };
  const profil = compactSaatlikProfil(blok.doluluk_profili || {});
  const ozet = {
    arac: blok.arac_sayisi ?? blok.arac,
    sefer: blok.toplam_sefer ?? blok.sefer,
    km: blok.km,
    maliyet: blok.maliyet,
    tipler: blok.arac_tiplere || blok.tipler || {},
    kirilim: blok.kirilim || null,
    vehicle_type_code: blok.vehicle_type_code,
    hedef_ustu_yol_orani: profil.hedef_ustu_yol_orani,
    max_doluluk: profil.max_doluluk,
    doluluk_profili: profil,
    donemsel: blok.donemsel || null,
    uyari: blok.uyari || [],
  };
  const fark = blok.fark;
  if (fark) {
    ozet.fark_arac = fark.arac;
    ozet.fark_sefer = fark.sefer;
    ozet.fark_km = fark.km;
    ozet.fark_maliyet = fark.maliyet;
    ozet.fark_yuzde = fark.maliyet_yuzde;
    ozet.tasarruf_frekans = fark.tasarruf_frekans;
    ozet.tasarruf_tip = fark.tasarruf_tip;
    ozet.fark_yillik = (blok.donemsel || {}).yillik;
    ozet.fark_aylik = (blok.donemsel || {}).aylik;
  }
  return ozet;
}

export function hatOzetiFromYanit(yanit, hatNo, gun, hatAdi) {
  if (!yanit || typeof yanit !== "object") return null;
  const uyari = [...(yanit.uyari || [])];
  const senaryolar = {};
  const sira = yanit.senaryo_sirasi || Object.keys(yanit.senaryolar || {});
  for (const kimlik of sira) {
    const blok = (yanit.senaryolar || {})[kimlik] || {};
    senaryolar[kimlik] = senaryoOzeti(blok);
    for (const kod of blok.uyari || []) {
      if (!uyari.includes(kod)) uyari.push(kod);
    }
  }
  return {
    hat_no: String(hatNo),
    hat_adi: hatAdi || null,
    gun,
    hedef_doluluk: yanit.hedef_doluluk,
    maliyet_modeli: yanit.maliyet_modeli,
    senaryo_sirasi: [...sira],
    senaryolar,
    uyari,
    durum: "done",
  };
}

/**
 * Ağ koşusu özeti (backend) saatlik satırlarında `yuk` taşımaz; grafik için
 * koltuk × max_doluluk ile doldur. Zaten kompakt ise idempotent.
 */
export function normalizeHatOzeti(ozet) {
  if (!ozet || typeof ozet !== "object") return ozet;
  const senaryolar = { ...(ozet.senaryolar || {}) };
  for (const [id, blok] of Object.entries(senaryolar)) {
    if (!blok || typeof blok !== "object" || blok.hata != null) continue;
    const rawProfil = blok.doluluk_profili || {
      hedef_ustu_yol_orani: blok.hedef_ustu_yol_orani,
      max_doluluk: blok.max_doluluk,
      saatlik: [],
    };
    const profil = compactSaatlikProfil(rawProfil);
    senaryolar[id] = {
      ...blok,
      hedef_ustu_yol_orani: blok.hedef_ustu_yol_orani ?? profil.hedef_ustu_yol_orani,
      max_doluluk: blok.max_doluluk ?? profil.max_doluluk,
      doluluk_profili: profil,
    };
  }
  return { ...ozet, senaryolar };
}

export function agToplamiFromOzetler(ozetler) {
  const sonuc = {};
  const sira = [];
  for (const ozet of ozetler || []) {
    const senaryolar = ozet.senaryolar || {};
    const mevcut = senaryolar[CURRENT_ID] || {};
    if (mevcut.hata != null || !Object.keys(mevcut).length) continue;
    for (const kimlik of ozet.senaryo_sirasi || Object.keys(senaryolar)) {
      if (!sira.includes(kimlik)) sira.push(kimlik);
      const blok = senaryolar[kimlik] || {};
      if (!blok || blok.hata != null) continue;
      const hedef = (sonuc[kimlik] ||= {
        hat_sayisi: 0,
        mevcut_maliyet: 0,
        arac: 0,
        sefer: 0,
        km: 0,
        maliyet: 0,
        ...(kimlik === CURRENT_ID
          ? {}
          : { fark_arac: 0, fark_sefer: 0, fark_km: 0, fark_maliyet: 0, fark_yillik: 0 }),
      });
      hedef.hat_sayisi += 1;
      hedef.mevcut_maliyet += num(mevcut.maliyet);
      hedef.arac += num(blok.arac);
      hedef.sefer += num(blok.sefer);
      hedef.km += num(blok.km);
      hedef.maliyet += num(blok.maliyet);
      if (kimlik !== CURRENT_ID) {
        hedef.fark_arac += num(blok.fark_arac);
        hedef.fark_sefer += num(blok.fark_sefer);
        hedef.fark_km += num(blok.fark_km);
        hedef.fark_maliyet += num(blok.fark_maliyet);
        hedef.fark_yillik += num(blok.fark_yillik);
      }
    }
  }
  for (const [kimlik, hedef] of Object.entries(sonuc)) {
    if (kimlik !== CURRENT_ID) {
      const taban = hedef.mevcut_maliyet;
      hedef.fark_yuzde = taban > 0 ? (hedef.fark_maliyet / taban) * 100 : null;
    }
  }
  const dahil = Object.values(ozetler || {}).filter((o) => o?.durum === "done" || o?.senaryolar).length;
  return {
    senaryo_sirasi: sira.filter((k) => sonuc[k]),
    senaryolar: sonuc,
    dahil_hat: dahil,
  };
}

export function cheapestScenarioId(agToplami) {
  const bloklar = agToplami?.senaryolar || {};
  let best = null;
  let bestCost = Infinity;
  for (const [id, blok] of Object.entries(bloklar)) {
    if (id === CURRENT_ID || blok?.hata) continue;
    const maliyet = num(blok.maliyet);
    if (maliyet < bestCost) {
      bestCost = maliyet;
      best = id;
    }
  }
  return best;
}

export function extractSenaryoYaniti(data) {
  if (!data || typeof data !== "object") return null;
  if (data.senaryolar && typeof data.senaryolar === "object") {
    return {
      senaryolar: data.senaryolar,
      senaryo_sirasi: data.senaryo_sirasi,
      maliyet_modeli: data.maliyet_modeli,
      hedef_doluluk: data.hedef_doluluk,
      uyari: data.uyari,
    };
  }
  if (data.senaryoYaniti) return extractSenaryoYaniti(data.senaryoYaniti);
  if (data.optimization) return extractSenaryoYaniti(data.optimization);
  if (data.sonuclar) return extractSenaryoYaniti(data.sonuclar);
  return null;
}
